import {
  SectionSortAdditionalDetails,
  SectionSortOption,
  SectionSortProductInfo,
  SectionSortSecondaryCannabinoids,
  SectionSortTerpenes
} from '../models/enum/dto/section-sort-option.enum';
import { Move } from './sort-utils';
import { LocationPriceStream } from '../models/enum/shared/location-price-stream';
import type { SectionWithProducts } from '../models/menu/section/section-with-products';
import { Menu } from '../models/menu/menu';
import { VariantGroup } from '../models/product/dto/variant-group';
import { SortVariantsInSectionUtils } from './sort-variants-in-section-utils';
import { Cannabinoid, SecondaryCannabinoid } from '../models/enum/shared/cannabinoid';
import { SectionColumnConfigProductInfoKey } from '../models/enum/shared/section-column-config-product-info-key';
import { SectionColumnConfigDataForSecondaryPriceColumn } from '../models/enum/shared/section-column-config-data-value';

// @dynamic
export class SortVariantGroupUtils extends SortVariantsInSectionUtils {
  /* *******************************************************************************************
   *                             Variant Groups - Sort Options                                 *
   * *******************************************************************************************/

  static variantGroupsBrandAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupBrand(), b?.getGroupBrand());
  };
  static variantGroupsBrandDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupBrand(), b?.getGroupBrand());
  };

  static variantGroupsClassificationAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.strainTypeSortAsc(a?.getGroupClassification(), b?.getGroupClassification());
  };
  static variantGroupsClassificationDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.strainTypeSortDesc(a?.getGroupClassification(), b?.getGroupClassification());
  };

  static variantGroupsLineageAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupLineage(), b?.getGroupLineage());
  };
  static variantGroupsLineageDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupLineage(), b?.getGroupLineage());
  };

  static variantGroupsManufacturerAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupManufacturer(), b?.getGroupManufacturer());
  };
  static variantGroupsManufacturerDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupManufacturer(), b?.getGroupManufacturer());
  };

  static variantGroupsPackagedQuantityAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberAscending(a?.getMinPackageQuantity(), b?.getMinPackageQuantity());
  };
  static variantGroupsPackagedQuantityDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberDescending(a?.getMaxPackageQuantity(), b?.getMaxPackageQuantity());
  };

  static variantGroupsPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    const aGroupPrice = a?.getMinGroupPrice(themeId, locationId, companyId, locationPriceStream, hideSale);
    const bGroupPrice = b?.getMinGroupPrice(themeId, locationId, companyId, locationPriceStream, hideSale);
    return SortVariantGroupUtils.numberAscending(aGroupPrice, bGroupPrice);
  };
  static variantGroupsPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    const aGroupPrice = a?.getMinGroupPrice(themeId, locationId, companyId, locationPriceStream, hideSale);
    const bGroupPrice = b?.getMinGroupPrice(themeId, locationId, companyId, locationPriceStream, hideSale);
    return SortVariantGroupUtils.numberDescending(aGroupPrice, bGroupPrice);
  };

  static variantGroupsPricePerUOMAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locationId, companyId]: [string, number, number],
    [locationPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    const aMinGroupPricePerUOM = a?.getMinGroupPricePerUOM(tId, locationId, companyId, locationPriceStream, hideSale);
    const bMinGroupPricePerUOM = b?.getMinGroupPricePerUOM(tId, locationId, companyId, locationPriceStream, hideSale);
    return SortVariantGroupUtils.numberAscending(aMinGroupPricePerUOM, bMinGroupPricePerUOM);
  };
  static variantGroupsPricePerUOMDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locationId, companyId]: [string, number, number],
    [locationPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    const bMaxGroupPricePerUOM = b?.getMaxGroupPricePerUOM(tId, locationId, companyId, locationPriceStream, hideSale);
    const aMaxGroupPricePerUOM = a?.getMaxGroupPricePerUOM(tId, locationId, companyId, locationPriceStream, hideSale);
    return SortVariantGroupUtils.numberDescending(aMaxGroupPricePerUOM, bMaxGroupPricePerUOM);
  };

  static variantGroupsOriginalPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinOriginalPrice = a?.getMinOriginalPrice(themeId, locationId, companyId, locationPriceStream);
    const bMinOriginalPrice = b?.getMinOriginalPrice(themeId, locationId, companyId, locationPriceStream);
    return SortVariantGroupUtils.numberAscending(aMinOriginalPrice, bMinOriginalPrice);
  };
  static variantGroupsOriginalPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinOriginalPrice = a?.getMaxOriginalPrice(themeId, locationId, companyId, locationPriceStream);
    const bMinOriginalPrice = b?.getMaxOriginalPrice(themeId, locationId, companyId, locationPriceStream);
    return SortVariantGroupUtils.numberDescending(aMinOriginalPrice, bMinOriginalPrice);
  };

  static variantGroupsSaleOriginalPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinSaleOriginalPrice = a?.getMinSaleOriginalPrice(tId, locationId, companyId, locationPriceStream);
    const bMinSaleOriginalPrice = b?.getMinSaleOriginalPrice(tId, locationId, companyId, locationPriceStream);
    return SortVariantGroupUtils.numberAscending(aMinSaleOriginalPrice, bMinSaleOriginalPrice);
  };
  static variantGroupsSaleOriginalPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinSaleOriginalPrice = a?.getMaxSaleOriginalPrice(tId, locationId, companyId, locationPriceStream);
    const bMinSaleOriginalPrice = b?.getMaxSaleOriginalPrice(tId, locationId, companyId, locationPriceStream);
    return SortVariantGroupUtils.numberDescending(aMinSaleOriginalPrice, bMinSaleOriginalPrice);
  };

  static variantGroupsOriginalAndSalePriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinOriginalAndSalePrice = a?.getMinGroupPrice(themeId, locationId, companyId, locationPriceStream, false);
    const bMinOriginalAndSalePrice = b?.getMinGroupPrice(themeId, locationId, companyId, locationPriceStream, false);
    return SortVariantGroupUtils.numberAscending(aMinOriginalAndSalePrice, bMinOriginalAndSalePrice);
  };
  static variantGroupsOriginalAndSalePriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinOriginalAndSalePrice = a?.getMaxGroupPrice(themeId, locationId, companyId, locationPriceStream, false);
    const bMinOriginalAndSalePrice = b?.getMaxGroupPrice(themeId, locationId, companyId, locationPriceStream, false);
    return SortVariantGroupUtils.numberDescending(aMinOriginalAndSalePrice, bMinOriginalAndSalePrice);
  };

  static variantGroupsTaxesInPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number]
  ): Move => {
    const aMinTaxesInPrice = a?.getMinTaxesInPrice(themeId, locationId, companyId);
    const bMinTaxesInPrice = b?.getMinTaxesInPrice(themeId, locationId, companyId);
    return SortVariantGroupUtils.numberAscending(aMinTaxesInPrice, bMinTaxesInPrice);
  };
  static variantGroupsTaxesInPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number]
  ): Move => {
    const aMinTaxesInPrice = a?.getMaxTaxesInPrice(themeId, locationId, companyId);
    const bMinTaxesInPrice = b?.getMaxTaxesInPrice(themeId, locationId, companyId);
    return SortVariantGroupUtils.numberDescending(aMinTaxesInPrice, bMinTaxesInPrice);
  };

  static variantGroupsTaxesInRoundedPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number]
  ): Move => {
    const aMinTaxesInPrice = a?.getMinTaxesInPrice(themeId, locationId, companyId);
    const bMinTaxesInPrice = b?.getMinTaxesInPrice(themeId, locationId, companyId);
    return SortVariantGroupUtils.numberAscending(aMinTaxesInPrice, bMinTaxesInPrice);
  };
  static variantGroupsTaxesInRoundedPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number]
  ): Move => {
    const aMinTaxesInPrice = a?.getMaxTaxesInRoundedPrice(themeId, locationId, companyId);
    const bMinTaxesInPrice = b?.getMaxTaxesInRoundedPrice(themeId, locationId, companyId);
    return SortVariantGroupUtils.numberDescending(aMinTaxesInPrice, bMinTaxesInPrice);
  };

  static variantGroupsPreTaxPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number]
  ): Move => {
    const aMinPreTaxPrice = a?.getMinPreTaxPrice(themeId, locationId, companyId);
    const bMinPreTaxPrice = b?.getMinPreTaxPrice(themeId, locationId, companyId);
    return SortVariantGroupUtils.numberAscending(aMinPreTaxPrice, bMinPreTaxPrice);
  };
  static variantGroupsPreTaxPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number]
  ): Move => {
    const aMinPreTaxPrice = a?.getMaxPreTaxPrice(themeId, locationId, companyId);
    const bMinPreTaxPrice = b?.getMaxPreTaxPrice(themeId, locationId, companyId);
    return SortVariantGroupUtils.numberDescending(aMinPreTaxPrice, bMinPreTaxPrice);
  };

  static variantGroupsMinSecondaryPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinGroupSecondaryPrice = a?.getMinGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    const bMinGroupSecondaryPrice = b?.getMinGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    return SortVariantGroupUtils.numberAscending(aMinGroupSecondaryPrice, bMinGroupSecondaryPrice);
  };
  static variantGroupsMinSecondaryPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMinGroupSecondaryPrice = a?.getMinGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    const bMinGroupSecondaryPrice = b?.getMinGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    return SortVariantGroupUtils.numberDescending(aMinGroupSecondaryPrice, bMinGroupSecondaryPrice);
  };

  static variantGroupsMaxSecondaryPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMaxGroupSecondaryPrice = a?.getMaxGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    const bMaxGroupSecondaryPrice = b?.getMaxGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    return SortVariantGroupUtils.numberAscending(aMaxGroupSecondaryPrice, bMaxGroupSecondaryPrice);
  };
  static variantGroupsMaxSecondaryPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [locationPriceStream]: [LocationPriceStream]
  ): Move => {
    const aMaxGroupSecondaryPrice = a?.getMaxGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    const bMaxGroupSecondaryPrice = b?.getMaxGroupSecondaryPrice(themeId, companyId, locationId, locationPriceStream);
    return SortVariantGroupUtils.numberDescending(aMaxGroupSecondaryPrice, bMaxGroupSecondaryPrice);
  };

  static variantGroupsProductTypeAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupProductType(), b?.getGroupProductType());
  };
  static variantGroupsProductTypeDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupProductType(), b?.getGroupProductType());
  };

  static variantGroupsSecondaryPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locId, compId]: [string, number, number],
    [section, priceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move => {
    const secondaryPriceState = section?.columnConfig?.get(SectionColumnConfigProductInfoKey.SecondaryPrice)?.dataValue;
    switch (secondaryPriceState) {
      case SectionColumnConfigDataForSecondaryPriceColumn.PricePerUOM:
        return SortVariantGroupUtils.variantGroupsPricePerUOMAsc([a, b], [tId, locId, compId], [priceStream, hideSale]);
      case SectionColumnConfigDataForSecondaryPriceColumn.OriginalPrice:
        return SortVariantGroupUtils.variantGroupsOriginalPriceAsc([a, b], [tId, locId, compId], [priceStream]);
      case SectionColumnConfigDataForSecondaryPriceColumn.SaleOriginalPrice:
        return SortVariantGroupUtils.variantGroupsSaleOriginalPriceAsc([a, b], [tId, locId, compId], [priceStream]);
      case SectionColumnConfigDataForSecondaryPriceColumn.OriginalAndSalePrice:
        return SortVariantGroupUtils.variantGroupsOriginalAndSalePriceAsc([a, b], [tId, locId, compId], [priceStream]);
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInPrice:
        return SortVariantGroupUtils.variantGroupsTaxesInPriceAsc([a, b], [tId, locId, compId]);
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInRoundedPrice:
        return SortVariantGroupUtils.variantGroupsTaxesInRoundedPriceAsc([a, b], [tId, locId, compId]);
      case SectionColumnConfigDataForSecondaryPriceColumn.PreTaxPrice:
        return SortVariantGroupUtils.variantGroupsPreTaxPriceAsc([a, b], [tId, locId, compId]);
      default:
        return SortVariantGroupUtils.variantGroupsMinSecondaryPriceAsc([a, b], [tId, locId, compId], [priceStream]);
    }
  };
  static variantGroupsSecondaryPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locId, cId]: [string, number, number],
    [section, priceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move => {
    const secondaryPriceState = section?.columnConfig?.get(SectionColumnConfigProductInfoKey.SecondaryPrice)?.dataValue;
    switch (secondaryPriceState) {
      case SectionColumnConfigDataForSecondaryPriceColumn.PricePerUOM:
        return SortVariantGroupUtils.variantGroupsPricePerUOMDesc([a, b], [tId, locId, cId], [priceStream, hideSale]);
      case SectionColumnConfigDataForSecondaryPriceColumn.OriginalPrice:
        return SortVariantGroupUtils.variantGroupsOriginalPriceDesc([a, b], [tId, locId, cId], [priceStream]);
      case SectionColumnConfigDataForSecondaryPriceColumn.SaleOriginalPrice:
        return SortVariantGroupUtils.variantGroupsSaleOriginalPriceDesc([a, b], [tId, locId, cId], [priceStream]);
      case SectionColumnConfigDataForSecondaryPriceColumn.OriginalAndSalePrice:
        return SortVariantGroupUtils.variantGroupsOriginalAndSalePriceDesc([a, b], [tId, locId, cId], [priceStream]);
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInPrice:
        return SortVariantGroupUtils.variantGroupsTaxesInPriceDesc([a, b], [tId, locId, cId]);
      case SectionColumnConfigDataForSecondaryPriceColumn.TaxesInRoundedPrice:
        return SortVariantGroupUtils.variantGroupsTaxesInRoundedPriceDesc([a, b], [tId, locId, cId]);
      case SectionColumnConfigDataForSecondaryPriceColumn.PreTaxPrice:
        return SortVariantGroupUtils.variantGroupsPreTaxPriceDesc([a, b], [tId, locId, cId]);
      default:
        return SortVariantGroupUtils.variantGroupsMaxSecondaryPriceDesc([a, b], [tId, locId, cId], [priceStream]);
    }
  };

  static variantGroupsStockAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberAscending(a?.getMaxVariantStock(), b?.getMaxVariantStock());
  };
  static variantGroupsStockDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberDescending(a?.getMaxVariantStock(), b?.getMaxVariantStock());
  };

  static variantGroupsTitleAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupTitle(), b?.getGroupTitle());
  };
  static variantGroupsTitleDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupTitle(), b?.getGroupTitle());
  };

  static variantGroupsUnitSizeAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberAscending(a?.getMinNumericUnitSize(), b?.getMinNumericUnitSize());
  };
  static variantGroupsUnitSizeDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberDescending(a?.getMaxNumericUnitSize(), b?.getMaxNumericUnitSize());
  };

  static variantGroupsEffectsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupEffects(), b?.getGroupEffects());
  };
  static variantGroupsEffectsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupEffects(), b?.getGroupEffects());
  };

  static variantGroupsAromasAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupAromas(), b?.getGroupAromas());
  };
  static variantGroupsAromasDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupAromas(), b?.getGroupAromas());
  };

  static variantGroupsFlavorGroupAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupFlavorGroup(), b?.getGroupFlavorGroup());
  };
  static variantGroupsFlavorGroupDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupFlavorGroup(), b?.getGroupFlavorGroup());
  };

  static variantGroupsFlavorsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupFlavors(), b?.getGroupFlavors());
  };
  static variantGroupsFlavorsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupFlavors(), b?.getGroupFlavors());
  };

  static variantGroupsHelpsWithAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupHelpsWith(), b?.getGroupHelpsWith());
  };
  static variantGroupsHelpsWithDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupHelpsWith(), b?.getGroupHelpsWith());
  };

  static variantGroupsAppearancesAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getGroupAppearances(), b?.getGroupAppearances());
  };
  static variantGroupsAppearancesDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getGroupAppearances(), b?.getGroupAppearances());
  };

  static variantGroupsGrowingConditionsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(
      a?.getGroupGrowingConditions(),
      b?.getGroupGrowingConditions()
    );
  };
  static variantGroupsGrowingConditionsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(
      a?.getGroupGrowingConditions(),
      b?.getGroupGrowingConditions()
    );
  };

  static variantGroupsEnergyScoreAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberAscendingNegativesAndNullsLast(a?.getMinEnergyScore(), b?.getMinEnergyScore());
  };
  static variantGroupsEnergyScoreDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.numberDescendingNegativesAndNullsLast(a?.getMaxEnergyScore(), b?.getMaxEnergyScore());
  };

  static variantGroupsCraftDesignationsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(
      a?.getGroupCraftDesignations(),
      b?.getGroupCraftDesignations()
    );
  };
  static variantGroupsCraftDesignationsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(
      a?.getGroupCraftDesignations(),
      b?.getGroupCraftDesignations()
    );
  };

  static variantGroupsTagsAsc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantListTagsAsc(a?.variants, b?.variants, section);
  };
  static variantGroupsTagsDesc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantListTagsDesc(a?.variants, b?.variants, section);
  };

  static variantGroupsTagGroupsAsc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantListTagGroupsAsc(a?.variants, b?.variants, section);
  };
  static variantGroupsTagGroupsDesc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantListTagGroupsDesc(a?.variants, b?.variants, section);
  };

  static variantGroupCannabinoidAsc = (a: VariantGroup, b: VariantGroup, cannabinoid: string): Move => {
    return SortVariantGroupUtils.numberAscending(
      a?.getMinNumericCannabinoid(cannabinoid),
      b?.getMinNumericCannabinoid(cannabinoid)
    );
  };
  static variantGroupCannabinoidDesc = (a: VariantGroup, b: VariantGroup, cannabinoid: string): Move => {
    return SortVariantGroupUtils.numberAscending(
      a?.getMaxNumericCannabinoid(cannabinoid),
      b?.getMaxNumericCannabinoid(cannabinoid)
    );
  };

  static variantGroupTACAsc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.numberAscendingNegativesAndZerosAndNullsLast(
      a?.getMinNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids),
      b?.getMinNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids)
    );
  };
  static variantGroupTACDesc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.numberDescendingNegativesAndZerosAndNullsLast(
      a?.getMaxNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids),
      b?.getMaxNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids)
    );
  };

  static variantGroupTopCannabinoidAsc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(
      a?.getVariantGroupTopCannabinoid(enabledSecondaryCannabinoids, ignoredListCannabinoids),
      b?.getVariantGroupTopCannabinoid(enabledSecondaryCannabinoids, ignoredListCannabinoids)
    );
  };
  static variantGroupTopCannabinoidDesc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(
      a?.getVariantGroupTopCannabinoid(enabledSecondaryCannabinoids, ignoredListCannabinoids),
      b?.getVariantGroupTopCannabinoid(enabledSecondaryCannabinoids, ignoredListCannabinoids)
    );
  };

  static variantGroupTerpeneAsc = (a: VariantGroup, b: VariantGroup, terpeneCamelCased: string): Move => {
    return SortVariantGroupUtils.numberAscending(
      a?.getMinNumericTerpene(terpeneCamelCased),
      b?.getMinNumericTerpene(terpeneCamelCased)
    );
  };
  static variantGroupTerpeneDesc = (a: VariantGroup, b: VariantGroup, terpeneCamelCased: string): Move => {
    return SortVariantGroupUtils.numberAscending(
      a?.getMaxNumericTerpene(terpeneCamelCased),
      b?.getMaxNumericTerpene(terpeneCamelCased)
    );
  };

  static variantGroupTATAsc = (a: VariantGroup, b: VariantGroup, enabledTerpenesPascalCased: string[]): Move => {
    return SortVariantGroupUtils.numberAscendingNegativesAndZerosAndNullsLast(
      a?.getMinNumericTotalActiveTerpenes(enabledTerpenesPascalCased),
      b?.getMinNumericTotalActiveTerpenes(enabledTerpenesPascalCased)
    );
  };
  static variantGroupTATDesc = (a: VariantGroup, b: VariantGroup, enabledTerpenesPascalCased: string[]): Move => {
    return SortVariantGroupUtils.numberDescendingNegativesAndZerosAndNullsLast(
      a?.getMaxNumericTotalActiveTerpenes(enabledTerpenesPascalCased),
      b?.getMaxNumericTotalActiveTerpenes(enabledTerpenesPascalCased)
    );
  };

  static variantGroupTopTerpeneAsc = (a: VariantGroup, b: VariantGroup, enabledTerpenesPascalCased: string[]): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(
      a?.getVariantGroupTopTerpene(enabledTerpenesPascalCased),
      b?.getVariantGroupTopTerpene(enabledTerpenesPascalCased)
    );
  };
  static variantGroupTopTerpeneDesc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledTerpenesPascalCased: string[]
  ): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(
      a?.getVariantGroupTopTerpene(enabledTerpenesPascalCased),
      b?.getVariantGroupTopTerpene(enabledTerpenesPascalCased)
    );
  };

  /* *******************************************************************************************
   *                          Section Variant Groups - Sort Options                            *
   * *******************************************************************************************/

  static sectionVariantGroupsBrandAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsBrandAsc(a, b);
  };
  static sectionVariantGroupsBrandDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsBrandDesc(a, b);
  };

  static sectionVariantGroupsClassificationAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsClassificationAsc(a, b);
  };
  static sectionVariantGroupsClassificationDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsClassificationDesc(a, b);
  };

  static sectionVariantGroupsLineageAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsLineageAsc(a, b);
  };
  static sectionVariantGroupsLineageDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsLineageDesc(a, b);
  };

  static sectionVariantGroupsManufacturerAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsManufacturerAsc(a, b);
  };
  static sectionVariantGroupsManufacturerDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsManufacturerDesc(a, b);
  };

  static sectionVariantGroupsPackagedQuantityAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsPackagedQuantityAsc(a, b);
  };
  static sectionVariantGroupsPackagedQuantityDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsPackagedQuantityDesc(a, b);
  };

  static sectionVariantGroupsPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locId, compId]: [string, number, number],
    [locationPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    return SortVariantGroupUtils.variantGroupsPriceAsc([a, b], [tId, locId, compId], [locationPriceStream, hideSale]);
  };
  static sectionVariantGroupsPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [tId, locId, compId]: [string, number, number],
    [locationPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    return SortVariantGroupUtils.variantGroupsPriceDesc([a, b], [tId, locId, compId], [locationPriceStream, hideSale]);
  };

  static sectionVariantGroupsProductTypeAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsProductTypeAsc(a, b);
  };
  static sectionVariantGroupsProductTypeDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsProductTypeDesc(a, b);
  };

  static sectionVariantGroupsSecondaryPriceAsc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themId, locationId, companyId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move => {
    return SortVariantGroupUtils.variantGroupsSecondaryPriceAsc(
      [a, b],
      [themId, locationId, companyId],
      [section, locationPriceStream, hideSale]
    );
  };
  static sectionVariantGroupsSecondaryPriceDesc = (
    [a, b]: [VariantGroup, VariantGroup],
    [themId, locationId, companyId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move => {
    return SortVariantGroupUtils.variantGroupsSecondaryPriceDesc(
      [a, b],
      [themId, locationId, companyId],
      [section, locationPriceStream, hideSale]
    );
  };

  static sectionVariantGroupsStockAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsStockAsc(a, b);
  };
  static sectionVariantGroupsStockDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsStockDesc(a, b);
  };

  static sectionVariantGroupsTitleAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsTitleAsc(a, b);
  };
  static sectionVariantGroupsTitleDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsTitleDesc(a, b);
  };

  static sectionVariantGroupsUnitSizeAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsUnitSizeAsc(a, b);
  };
  static sectionVariantGroupsUnitSizeDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsUnitSizeDesc(a, b);
  };

  static sectionVariantGroupsEffectsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsEffectsAsc(a, b);
  };
  static sectionVariantGroupsEffectsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsEffectsDesc(a, b);
  };

  static sectionVariantGroupsAromasAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsAromasAsc(a, b);
  };
  static sectionVariantGroupsAromasDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsAromasDesc(a, b);
  };

  static sectionVariantGroupsFlavorGroupAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsFlavorGroupAsc(a, b);
  };
  static sectionVariantGroupsFlavorGroupDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsFlavorGroupDesc(a, b);
  };

  static sectionVariantGroupsFlavorsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsFlavorsAsc(a, b);
  };
  static sectionVariantGroupsFlavorsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsFlavorsDesc(a, b);
  };

  static sectionVariantGroupsHelpsWithAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsHelpsWithAsc(a, b);
  };
  static sectionVariantGroupsHelpsWithDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsHelpsWithDesc(a, b);
  };

  static sectionVariantGroupsAppearancesAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsAppearancesAsc(a, b);
  };
  static sectionVariantGroupsAppearancesDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsAppearancesDesc(a, b);
  };

  static sectionVariantGroupsGrowingConditionsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsGrowingConditionsAsc(a, b);
  };
  static sectionVariantGroupsGrowingConditionsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsGrowingConditionsDesc(a, b);
  };

  static sectionVariantGroupsEnergyScoreAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsEnergyScoreAsc(a, b);
  };
  static sectionVariantGroupsEnergyScoreDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsEnergyScoreDesc(a, b);
  };

  static sectionVariantGroupsCraftDesignationsAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsCraftDesignationsAsc(a, b);
  };
  static sectionVariantGroupsCraftDesignationsDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.variantGroupsCraftDesignationsDesc(a, b);
  };

  static sectionVariantGroupsTagsAsc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantGroupsTagsAsc(a, b, section);
  };
  static sectionVariantGroupsTagsDesc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantGroupsTagsDesc(a, b, section);
  };

  static sectionVariantGroupsTagGroupsAsc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantGroupsTagGroupsAsc(a, b, section);
  };
  static sectionVariantGroupsTagGroupsDesc = (a: VariantGroup, b: VariantGroup, section: SectionWithProducts): Move => {
    return SortVariantGroupUtils.variantGroupsTagGroupsDesc(a, b, section);
  };

  static sectionVariantGroupsCannabinoidAsc = (a: VariantGroup, b: VariantGroup, cannabinoid: string): Move => {
    return SortVariantGroupUtils.variantGroupCannabinoidAsc(a, b, cannabinoid);
  };
  static sectionVariantGroupsCannabinoidDesc = (a: VariantGroup, b: VariantGroup, cannabinoid: string): Move => {
    return SortVariantGroupUtils.variantGroupCannabinoidDesc(a, b, cannabinoid);
  };

  static sectionVariantGroupsTACAsc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTACAsc(a, b, enabledSecondaryCannabinoids);
  };
  static sectionVariantGroupsTACDesc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTACDesc(a, b, enabledSecondaryCannabinoids);
  };

  static sectionVariantGroupsTopCannabinoidAsc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTopCannabinoidAsc(
      a,
      b,
      enabledSecondaryCannabinoids,
      ignoredListCannabinoids
    );
  };
  static sectionVariantGroupsTopCannabinoidDesc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTopCannabinoidDesc(
      a,
      b,
      enabledSecondaryCannabinoids,
      ignoredListCannabinoids
    );
  };

  static sectionVariantGroupsTerpeneAsc = (a: VariantGroup, b: VariantGroup, terpeneCamelCased: string): Move => {
    return SortVariantGroupUtils.variantGroupTerpeneAsc(a, b, terpeneCamelCased);
  };
  static sectionVariantGroupsTerpeneDesc = (a: VariantGroup, b: VariantGroup, terpeneCamelCased: string): Move => {
    return SortVariantGroupUtils.variantGroupTerpeneDesc(a, b, terpeneCamelCased);
  };

  static sectionVariantGroupsTATAsc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledTerpenesPascalCased: string[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTATAsc(a, b, enabledTerpenesPascalCased);
  };
  static sectionVariantGroupsTATDesc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledTerpenesPascalCased: string[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTATDesc(a, b, enabledTerpenesPascalCased);
  };

  static sectionVariantGroupsTopTerpeneAsc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledTerpenesPascalCased: string[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTopTerpeneAsc(a, b, enabledTerpenesPascalCased);
  };
  static sectionVariantGroupsTopTerpeneDesc = (
    a: VariantGroup,
    b: VariantGroup,
    enabledTerpenesPascalCased: string[]
  ): Move => {
    return SortVariantGroupUtils.variantGroupTopTerpeneDesc(a, b, enabledTerpenesPascalCased);
  };

  static sectionVariantGroupsVariantTypeAsc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringAscendingNullsLast(a?.getVariantType(), b?.getVariantType());
  };
  static sectionVariantGroupsVariantTypeDesc = (a: VariantGroup, b: VariantGroup): Move => {
    return SortVariantGroupUtils.stringDescendingNullsLast(a?.getVariantType(), b?.getVariantType());
  };

  private static sectionVariantGroupSorter(
    sortOption: SectionSortOption,
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move {
    switch (true) {
      case Object.values(SectionSortProductInfo).includes(sortOption as SectionSortProductInfo):
        return SortVariantGroupUtils.sectionVariantGroupProductInfoMove(
          sortOption,
          [a, b],
          [themeId, locationId, companyId],
          [section, locationPriceStream, hideSale]
        );
      case Object.values(SectionSortAdditionalDetails).includes(sortOption as SectionSortAdditionalDetails):
        return SortVariantGroupUtils.sectionVariantGroupAdditionalDetailsMove(sortOption, section, a, b);
      case Object.values(SectionSortSecondaryCannabinoids).includes(sortOption as SectionSortSecondaryCannabinoids):
        return SortVariantGroupUtils.sectionVariantGroupSecondaryCannabinoidMove(sortOption, a, b);
      case Object.values(SectionSortTerpenes).includes(sortOption as SectionSortTerpenes):
        return SortVariantGroupUtils.sectionVariantGroupTerpeneMove(sortOption, a, b);
    }
  }

  private static sectionVariantGroupProductInfoMove(
    sortOption: SectionSortOption,
    [a, b]: [VariantGroup, VariantGroup],
    [themeId, locationId, companyId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move {
    switch (sortOption) {
      case SectionSortProductInfo.BrandAsc:
        return SortVariantGroupUtils.sectionVariantGroupsBrandAsc(a, b);
      case SectionSortProductInfo.BrandDesc:
        return SortVariantGroupUtils.sectionVariantGroupsBrandDesc(a, b);
      // CBDAsc uses min CBD
      case SectionSortProductInfo.CBDAsc:
        return SortVariantGroupUtils.sectionVariantGroupsCannabinoidAsc(a, b, 'CBD');
      // CBDDesc uses max CBD
      case SectionSortProductInfo.CBDDesc:
        return SortVariantGroupUtils.sectionVariantGroupsCannabinoidDesc(a, b, 'CBD');
      case SectionSortProductInfo.ClassificationAsc:
        return SortVariantGroupUtils.sectionVariantGroupsClassificationAsc(a, b);
      case SectionSortProductInfo.ClassificationDesc:
        return SortVariantGroupUtils.sectionVariantGroupsClassificationDesc(a, b);
      case SectionSortProductInfo.LineageAsc:
        return SortVariantGroupUtils.sectionVariantGroupsLineageAsc(a, b);
      case SectionSortProductInfo.LineageDesc:
        return SortVariantGroupUtils.sectionVariantGroupsLineageDesc(a, b);
      case SectionSortProductInfo.ManufacturerAsc:
        return SortVariantGroupUtils.sectionVariantGroupsManufacturerAsc(a, b);
      case SectionSortProductInfo.ManufacturerDesc:
        return SortVariantGroupUtils.sectionVariantGroupsManufacturerDesc(a, b);
      // PackagedQuantityAsc uses min packaged quantity
      case SectionSortProductInfo.PackagedQuantityAsc:
        return SortVariantGroupUtils.sectionVariantGroupsPackagedQuantityAsc(a, b);
      // PackagedQuantityDesc uses max packaged quantity
      case SectionSortProductInfo.PackagedQuantityDesc:
        return SortVariantGroupUtils.sectionVariantGroupsPackagedQuantityDesc(a, b);
      // PriceAsc uses min price
      case SectionSortProductInfo.PriceAsc:
        return SortVariantGroupUtils.sectionVariantGroupsPriceAsc(
          [a, b],
          [themeId, locationId, companyId],
          [locationPriceStream, hideSale]
        );
      // PriceDesc uses max price
      case SectionSortProductInfo.PriceDesc:
        return SortVariantGroupUtils.sectionVariantGroupsPriceDesc(
          [a, b],
          [themeId, locationId, companyId],
          [locationPriceStream, hideSale]
        );
      case SectionSortProductInfo.ProductTypeAsc:
        return SortVariantGroupUtils.sectionVariantGroupsProductTypeAsc(a, b);
      case SectionSortProductInfo.ProductTypeDesc:
        return SortVariantGroupUtils.sectionVariantGroupsProductTypeDesc(a, b);
      // PriceAsc uses min secondary price
      case SectionSortProductInfo.SecondaryPriceAsc:
        return SortVariantGroupUtils.sectionVariantGroupsSecondaryPriceAsc(
          [a, b],
          [themeId, locationId, companyId],
          [section, locationPriceStream, hideSale]
        );
      // PriceDesc uses max secondary price
      case SectionSortProductInfo.SecondaryPriceDesc:
        return SortVariantGroupUtils.sectionVariantGroupsSecondaryPriceDesc(
          [a, b],
          [themeId, locationId, companyId],
          [section, locationPriceStream, hideSale]
        );
      case SectionSortProductInfo.StockAsc:
        return SortVariantGroupUtils.sectionVariantGroupsStockAsc(a, b);
      case SectionSortProductInfo.StockDesc:
        return SortVariantGroupUtils.sectionVariantGroupsStockDesc(a, b);
      case SectionSortProductInfo.TACAsc:
        return SortVariantGroupUtils.sectionVariantGroupsTACAsc(a, b, section.enabledSecondaryCannabinoids);
      case SectionSortProductInfo.TACDesc:
        return SortVariantGroupUtils.sectionVariantGroupsTACDesc(a, b, section.enabledSecondaryCannabinoids);
      case SectionSortProductInfo.TitleAsc:
        return SortVariantGroupUtils.sectionVariantGroupsTitleAsc(a, b);
      case SectionSortProductInfo.TitleDesc:
        return SortVariantGroupUtils.sectionVariantGroupsTitleDesc(a, b);
      // THCAsc uses min THC
      case SectionSortProductInfo.THCAsc:
        return SortVariantGroupUtils.sectionVariantGroupsCannabinoidAsc(a, b, 'THC');
      // THCDesc uses max THC
      case SectionSortProductInfo.THCDesc:
        return SortVariantGroupUtils.sectionVariantGroupsCannabinoidDesc(a, b, 'THC');
      case SectionSortProductInfo.TopCannabinoidAsc:
        return SortVariantGroupUtils.sectionVariantGroupsTopCannabinoidAsc(
          a,
          b,
          section.enabledSecondaryCannabinoids,
          section.ignoredListCannabinoids
        );
      case SectionSortProductInfo.TopCannabinoidDesc:
        return SortVariantGroupUtils.sectionVariantGroupsTopCannabinoidDesc(
          a,
          b,
          section.enabledSecondaryCannabinoids,
          section.ignoredListCannabinoids
        );
      case SectionSortProductInfo.TopTerpeneAsc:
        return SortVariantGroupUtils.sectionVariantGroupsTopTerpeneAsc(a, b, section.enabledTerpenesPascalCased);
      case SectionSortProductInfo.TopTerpeneDesc:
        return SortVariantGroupUtils.sectionVariantGroupsTopTerpeneDesc(a, b, section.enabledTerpenesPascalCased);
      case SectionSortProductInfo.TotalTerpeneAsc:
        return SortVariantGroupUtils.sectionVariantGroupsTATAsc(a, b, section.enabledTerpenesPascalCased);
      case SectionSortProductInfo.TotalTerpeneDesc:
        return SortVariantGroupUtils.sectionVariantGroupsTATDesc(a, b, section.enabledTerpenesPascalCased);
      // UnitSizeAsc uses min unit size
      case SectionSortProductInfo.UnitSizeAsc:
        return SortVariantGroupUtils.sectionVariantGroupsUnitSizeAsc(a, b);
      // UnitSizeDesc uses max unit size
      case SectionSortProductInfo.UnitSizeDesc:
        return SortVariantGroupUtils.sectionVariantGroupsUnitSizeDesc(a, b);
      case SectionSortProductInfo.VariantTypeAsc:
        return SortVariantGroupUtils.sectionVariantGroupsVariantTypeAsc(a, b);
      case SectionSortProductInfo.VariantTypeDesc:
        return SortVariantGroupUtils.sectionVariantGroupsVariantTypeDesc(a, b);
      default:
        return SortVariantGroupUtils.variantGroupsTitleAsc(a, b);
    }
  }

  private static sectionVariantGroupAdditionalDetailsMove(
    sortOption: SectionSortOption,
    section: SectionWithProducts,
    a: VariantGroup,
    b: VariantGroup
  ): Move {
    switch (sortOption) {
      case SectionSortAdditionalDetails.EffectsAsc:
        return SortVariantGroupUtils.sectionVariantGroupsEffectsAsc(a, b);
      case SectionSortAdditionalDetails.EffectsDesc:
        return SortVariantGroupUtils.sectionVariantGroupsEffectsDesc(a, b);
      case SectionSortAdditionalDetails.AromasAsc:
        return SortVariantGroupUtils.sectionVariantGroupsAromasAsc(a, b);
      case SectionSortAdditionalDetails.AromasDesc:
        return SortVariantGroupUtils.sectionVariantGroupsAromasDesc(a, b);
      case SectionSortAdditionalDetails.FlavorGroupAsc:
        return SortVariantGroupUtils.sectionVariantGroupsFlavorGroupAsc(a, b);
      case SectionSortAdditionalDetails.FlavorGroupDesc:
        return SortVariantGroupUtils.sectionVariantGroupsFlavorGroupDesc(a, b);
      case SectionSortAdditionalDetails.FlavorsAsc:
        return SortVariantGroupUtils.sectionVariantGroupsFlavorsAsc(a, b);
      case SectionSortAdditionalDetails.FlavorsDesc:
        return SortVariantGroupUtils.sectionVariantGroupsFlavorsDesc(a, b);
      case SectionSortAdditionalDetails.HelpsWithAsc:
        return SortVariantGroupUtils.sectionVariantGroupsHelpsWithAsc(a, b);
      case SectionSortAdditionalDetails.HelpsWithDesc:
        return SortVariantGroupUtils.sectionVariantGroupsHelpsWithDesc(a, b);
      case SectionSortAdditionalDetails.AppearancesAsc:
        return SortVariantGroupUtils.sectionVariantGroupsAppearancesAsc(a, b);
      case SectionSortAdditionalDetails.AppearancesDesc:
        return SortVariantGroupUtils.sectionVariantGroupsAppearancesDesc(a, b);
      case SectionSortAdditionalDetails.GrowingConditionsAsc:
        return SortVariantGroupUtils.sectionVariantGroupsGrowingConditionsAsc(a, b);
      case SectionSortAdditionalDetails.GrowingConditionsDesc:
        return SortVariantGroupUtils.sectionVariantGroupsGrowingConditionsDesc(a, b);
      case SectionSortAdditionalDetails.EnergyScoreAsc:
        return SortVariantGroupUtils.sectionVariantGroupsEnergyScoreAsc(a, b);
      case SectionSortAdditionalDetails.EnergyScoreDesc:
        return SortVariantGroupUtils.sectionVariantGroupsEnergyScoreDesc(a, b);
      case SectionSortAdditionalDetails.CraftDesignationsAsc:
        return SortVariantGroupUtils.sectionVariantGroupsCraftDesignationsAsc(a, b);
      case SectionSortAdditionalDetails.CraftDesignationsDesc:
        return SortVariantGroupUtils.sectionVariantGroupsCraftDesignationsDesc(a, b);
      case SectionSortAdditionalDetails.TagsAsc:
        return SortVariantGroupUtils.sectionVariantGroupsTagsAsc(a, b, section);
      case SectionSortAdditionalDetails.TagsDesc:
        return SortVariantGroupUtils.sectionVariantGroupsTagsDesc(a, b, section);
      case SectionSortAdditionalDetails.TagGroupsAsc:
        return SortVariantGroupUtils.sectionVariantGroupsTagGroupsAsc(a, b, section);
      case SectionSortAdditionalDetails.TagGroupsDesc:
        return SortVariantGroupUtils.sectionVariantGroupsTagGroupsDesc(a, b, section);
      default:
        return SortVariantGroupUtils.variantGroupsTitleAsc(a, b);
    }
  }

  private static sectionVariantGroupSecondaryCannabinoidMove(
    sortOption: SectionSortOption,
    a: VariantGroup,
    b: VariantGroup
  ): Move {
    const { cannabinoid, order } = SortVariantGroupUtils.getCannabinoidAccessor(sortOption);
    const isAscending = order === 'ASC';
    return isAscending
      ? SortVariantGroupUtils.sectionVariantGroupsCannabinoidAsc(a, b, cannabinoid)
      : SortVariantGroupUtils.sectionVariantGroupsCannabinoidDesc(a, b, cannabinoid);
  }

  private static sectionVariantGroupTerpeneMove(sortOption: SectionSortOption, a: VariantGroup, b: VariantGroup): Move {
    const { terpeneCamelCased, order } = SortVariantGroupUtils.getTerpeneAccessor(sortOption);
    const isAscending = order === 'ASC';
    return isAscending
      ? SortVariantGroupUtils.sectionVariantGroupsTerpeneAsc(a, b, terpeneCamelCased)
      : SortVariantGroupUtils.sectionVariantGroupsTerpeneDesc(a, b, terpeneCamelCased);
  }

  static variantGroupsBySortOptions = (
    variantGroups: VariantGroup[],
    menu: Menu,
    sec: SectionWithProducts,
    priceStream: LocationPriceStream
  ): VariantGroup[] => {
    const sorter = SortVariantGroupUtils.sectionVariantGroupSorter;
    const sortedGroups = SortVariantGroupUtils.sortVariantsInSection(sorter, variantGroups, menu, sec, priceStream);
    const sortVariantsInGrouping = (variantGroup: VariantGroup) => {
      variantGroup.sortVariantsBy(SortVariantGroupUtils.sortVariantsBySectionSortOptions, menu, sec, priceStream);
    };
    sortedGroups.forEach(sortVariantsInGrouping);
    return sortedGroups;
  };
}
